import { useState } from 'react';
import { TraitSummary } from '@/lib/types';

interface EmojiTraitFilterProps {
  traits: TraitSummary[];
  selectedTraits: Record<string, string[]>;
  onTraitToggle: (traitName: string, value: string) => void;
  onClearAll: () => void;
  isLoading?: boolean;
}

/**
 * Check if a string is an emoji (single emoji character/sequence)
 */
const isEmoji = (str: string): boolean => {
  // Remove any whitespace
  const trimmed = str.trim();
  if (!trimmed) return false;

  // Emoji regex pattern - matches most common emoji patterns
  const emojiPattern = /^(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F)(?:\u200D(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F))*$/u;
  return emojiPattern.test(trimmed) && trimmed.length <= 15; // Allow multi-codepoint emojis
};

/**
 * Check if all values in a trait are emojis
 */
const isEmojiTrait = (trait: TraitSummary): boolean => {
  if (trait.values.length === 0) return false;
  // Check first 10 values - if all are emojis, treat as emoji trait
  const samplesToCheck = trait.values.slice(0, 10);
  return samplesToCheck.every(v => isEmoji(v.value));
};

/**
 * Grid picker for a single trait type (works for emojis and short text values)
 */
function TraitGrid({
  trait,
  selectedValues,
  onToggle,
}: {
  trait: TraitSummary;
  selectedValues: string[];
  onToggle: (value: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const displayLimit = 50; // Show first 50, then "show more"

  const displayedValues = showAll ? trait.values : trait.values.slice(0, displayLimit);
  const hasMore = trait.values.length > displayLimit;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {displayedValues.map((traitValue) => {
          const isSelected = selectedValues.includes(traitValue.value);
          const valueIsEmoji = isEmoji(traitValue.value);
          return (
            <button
              key={traitValue.value}
              onClick={() => onToggle(traitValue.value)}
              title={`${traitValue.value} (${traitValue.count} items, ${traitValue.percentage.toFixed(1)}%)`}
              className={`
                ${valueIsEmoji ? 'w-10 h-10 text-2xl' : 'px-3 py-2 text-sm'}
                flex items-center justify-center rounded-lg
                transition-all duration-150 hover:scale-105
                ${isSelected
                  ? 'bg-blue-500 text-white ring-2 ring-blue-600 ring-offset-1'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'
                }
              `}
            >
              {traitValue.value}
            </button>
          );
        })}
      </div>

      {hasMore && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Show all ({trait.values.length})
        </button>
      )}

      {showAll && hasMore && (
        <button
          onClick={() => setShowAll(false)}
          className="text-sm text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
        >
          Show less
        </button>
      )}
    </div>
  );
}

/**
 * Selected emoji chips display
 */
function SelectedEmojis({
  selectedTraits,
  onRemove,
}: {
  selectedTraits: Record<string, string[]>;
  onRemove: (traitName: string, value: string) => void;
}) {
  const selections = Object.entries(selectedTraits).flatMap(([traitName, values]) =>
    values.map(value => ({ traitName, value }))
  );

  if (selections.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg mb-4">
      {selections.map(({ traitName, value }) => (
        <button
          key={`${traitName}-${value}`}
          onClick={() => onRemove(traitName, value)}
          className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 rounded-full shadow-sm hover:shadow transition-shadow text-sm"
          title={`Remove ${traitName}: ${value}`}
        >
          <span className="text-lg">{value}</span>
          <span className="text-gray-400 dark:text-gray-500 text-xs">{traitName}</span>
          <span className="text-gray-400 dark:text-gray-500 ml-1">×</span>
        </button>
      ))}
    </div>
  );
}

export default function EmojiTraitFilter({
  traits,
  selectedTraits,
  onTraitToggle,
  onClearAll,
  isLoading = false,
}: EmojiTraitFilterProps) {
  const [expandedTraits, setExpandedTraits] = useState<Set<string>>(new Set());

  const toggleExpand = (traitName: string) => {
    setExpandedTraits((prev) => {
      const next = new Set(prev);
      if (next.has(traitName)) {
        next.delete(traitName);
      } else {
        next.add(traitName);
      }
      return next;
    });
  };

  const hasSelections = Object.values(selectedTraits).some(arr => arr.length > 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 dark:text-white">Filters</h3>
        {hasSelections && (
          <button
            onClick={onClearAll}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Show selected values */}
      <SelectedEmojis
        selectedTraits={selectedTraits}
        onRemove={onTraitToggle}
      />

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading traits...</p>
      ) : traits.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">This collection has no filterable traits.</p>
      ) : (
        <div className="space-y-2">
          {/* All traits use the same grid picker */}
          {traits.map((trait) => {
            const selectedCount = selectedTraits[trait.name]?.length || 0;
            const isExpanded = expandedTraits.has(trait.name);

            return (
              <div key={trait.name} className="border-b border-gray-100 dark:border-gray-700 last:border-0 pb-2">
                <button
                  onClick={() => toggleExpand(trait.name)}
                  className="w-full flex items-center justify-between py-2 text-left"
                >
                  <span className="font-medium text-sm text-gray-700 dark:text-gray-300">
                    {trait.name}
                    {selectedCount > 0 && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">
                        {selectedCount}
                      </span>
                    )}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500">
                    {isExpanded ? '−' : '+'}
                  </span>
                </button>

                {isExpanded && (
                  <div className="pb-2">
                    <TraitGrid
                      trait={trait}
                      selectedValues={selectedTraits[trait.name] || []}
                      onToggle={(value) => onTraitToggle(trait.name, value)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { isEmojiTrait, isEmoji };
